import { HttpErrorResponse } from '@angular/common/http';

/**
 * Códigos de error del contrato de Tracking.
 *
 * El backend responde `{ "Error": "ReasonRequired" }` y **no manda copy**: el
 * texto que ve el usuario lo pone el front en su idioma. Por eso cada código
 * tiene aquí su clave de traducción.
 */
export const EXPENSE_ERROR_CODES = [
  'Unauthorized',
  'InvalidBody',
  'MissingRequiredFields',
  'InvalidAmount',
  'InvalidStatus',
  'ReasonRequired',
  'InvalidReceipt',
  'NotFound',
] as const;

export type ExpenseErrorCode = (typeof EXPENSE_ERROR_CODES)[number];

/** Código → clave de traducción. */
const ERROR_MESSAGE_KEY: Readonly<Record<ExpenseErrorCode, string>> = {
  Unauthorized: 'expenses.errors.unauthorized',
  InvalidBody: 'expenses.errors.invalid_body',
  MissingRequiredFields: 'expenses.errors.missing_fields',
  InvalidAmount: 'expenses.errors.invalid_amount',
  InvalidStatus: 'expenses.errors.invalid_status',
  ReasonRequired: 'expenses.errors.reason_required',
  InvalidReceipt: 'expenses.errors.invalid_receipt',
  NotFound: 'expenses.errors.not_found',
};

function isExpenseErrorCode(value: unknown): value is ExpenseErrorCode {
  return EXPENSE_ERROR_CODES.includes(value as ExpenseErrorCode);
}

/**
 * Extrae el código de error de una respuesta fallida.
 *
 * Las claves del JSON no distinguen mayúsculas en este contrato, así que se
 * aceptan `Error` y `error`. Devuelve `null` cuando la respuesta no trae un
 * código conocido: un fallo de red, un 502 del gateway o un despliegue que
 * introduzca un código nuevo caen aquí y se resuelven con el mensaje genérico,
 * en lugar de dejar al usuario sin explicación.
 */
export function expenseErrorCode(error: unknown): ExpenseErrorCode | null {
  const body = error instanceof HttpErrorResponse ? error.error : null;

  if (!body || typeof body !== 'object') {
    return null;
  }

  const raw =
    (body as Record<string, unknown>)['Error'] ??
    (body as Record<string, unknown>)['error'];

  return isExpenseErrorCode(raw) ? raw : null;
}

/**
 * Clave de traducción del detalle del error, o `null` si no se reconoce y hay
 * que quedarse con el mensaje de contexto de la operación.
 */
export function expenseErrorMessageKey(error: unknown): string | null {
  const code = expenseErrorCode(error);

  if (code) {
    return ERROR_MESSAGE_KEY[code];
  }

  // Sin código reconocible se distingue al menos el fallo del servidor.
  const status = error instanceof HttpErrorResponse ? error.status : null;

  return status !== null && status >= 500 ? 'expenses.errors.server' : null;
}
